import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MediaApi } from './MediaApi';
import { S3FileUploader } from '@/features/upload/S3FileUploader';
import { ensureValidCredentials } from '@/features/upload/useUploadCredentials';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { MEDIA_QUERY_KEYS } from '@/lib/constants/media';
import { FileStatusType } from '@lib2/model/media/FileStatusType';
import type { TextTrackCreateRequest, VideoViewData, ITextTrackData } from '@/lib/types/Media';
import type { TextTrackFormat, TextTrackType } from '@lib2/model/media/TextTrackTypes';

interface CreateTextTrackParams {
  req: TextTrackCreateRequest;
  file: File;
  contentType: string;
}

export function useCreateTextTrack(mediaId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ req, file, contentType }: CreateTextTrackParams) => {
      if (!selectedCId) throw new Error('No customer selected');

      // 1. Create text track record on backend → get S3 key
      const clientIngest = await MediaApi.createTextTrack(selectedCId, mediaId, req);

      if (!clientIngest.dashboard) {
        throw new Error('Backend returned no dashboard ingest data');
      }

      const { key } = clientIngest.dashboard;

      // 2. Upload file to S3
      const credentials = await ensureValidCredentials(selectedCId);
      await S3FileUploader.putObject(credentials, key, file, contentType);

      return clientIngest.dashboard.data;
    },
    onSuccess: (_data, variables) => {
      if (!selectedCId) return;
      const key = MEDIA_QUERY_KEYS.video(selectedCId, mediaId);

      // Optimistic cache update: show the track immediately.
      // The backend may need a moment to index, but we have all the data
      // from the request to construct a placeholder entry (fileId: 0).
      // Background invalidation replaces it with real server data.
      queryClient.setQueryData<VideoViewData>(key, (old) => {
        if (!old) return old;
        const optimisticTrack: ITextTrackData = {
          fileId: 0,
          mediaId,
          fileStatusType: FileStatusType.AwaitingIngest,
          format: variables.req.format as TextTrackFormat,
          langCode: variables.req.langCode,
          label: variables.req.label,
          ttType: variables.req.ttType as TextTrackType,
          remoteUrl: null,
        };
        return {
          ...old,
          texttracks: [...(old.texttracks ?? []), optimisticTrack],
        };
      });

      // Delayed invalidation: reconcile with real server data after backend indexes.
      // NOT immediate — an instant refetch would overwrite the optimistic entry
      // with stale server data (backend hasn't indexed the new track yet).
      setTimeout(() => queryClient.invalidateQueries({ queryKey: key }), 3000);
    },
  });
}

export function useDeleteTextTrack(mediaId: number) {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileId: number) => {
      if (!selectedCId) throw new Error('No customer selected');
      await MediaApi.deleteTextTrack(selectedCId, mediaId, fileId);
    },
    onSuccess: () => {
      if (selectedCId) {
        queryClient.invalidateQueries({
          queryKey: MEDIA_QUERY_KEYS.video(selectedCId, mediaId),
        });
      }
    },
  });
}
